import {inject, Pipe, PipeTransform} from '@angular/core';
import {Observable, of, Subscription} from 'rxjs';
import {shareReplay, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../components/destroyable';
import {PosFramesHistoryType} from '../../components/dex/dex-position-frames/pos-frames-history';
import {DexPosFramesStreamingService} from '../../components/dex/dex-streaming/dex-pos-frames-streaming.service';
import {
    getHeldPositionKey,
    HeldPosition,
    HeldPositionIdentifier
} from '../../components/dex/dex-streaming/types/held-position';
import {LiquidityPool} from '../../components/dex/dex-streaming/types/liquidity-pool';
import {Tranche} from '../../components/dex/dex-streaming/types/tranche';

@Pipe({
    name: 'dexTrancheSparkline',
    pure: true
})
export class DexTrancheSparklinePipe implements PipeTransform {

    private readonly dexPosFramesStreamingService = inject(DexPosFramesStreamingService);

    private prevPosIdentifierKey: string | undefined;

    private prevType: PosFramesHistoryType | undefined;

    private prevResult: Observable<number[] | undefined> | undefined;

    private readonly destroy$ = destroy$();

    private prevSub: Subscription;

    public transform(
        entity: Tranche | HeldPosition | LiquidityPool | undefined,
        isPnlType: boolean = false
    ): Observable<number[] | undefined> | undefined {
        const posIdentifier = this.getPosIdentifier(entity);

        if (!posIdentifier) {
            return undefined;
        }

        const key = posIdentifier ? getHeldPositionKey(posIdentifier) : undefined;
        const type = isPnlType ? PosFramesHistoryType.pnl : PosFramesHistoryType.yield;

        if (key === this.prevPosIdentifierKey && this.prevType === type) {
            return this.prevResult;
        }

        const sharedResult$ = this.createSharedFetching(
            this.getStream(posIdentifier, type).pipe(

                // TODO: [LP-SPARKLINE] Debug the shifted last tick in the sparkline (part 2/2)
                tap(vs => {
                    const v = vs?.length
                        ? vs[vs.length - 1]
                        : undefined;

                    if (isPnlType) {
                        (window as any).dexLpPnlLastPoint = v;
                        const log = (window as any).dexLpPnlLog;

                        if (log?.[0] && log[0].lastPoint === undefined) {
                            log[0].lastPoint = v;
                        }
                    }
                })
            )
        );

        this.prevResult = sharedResult$;
        this.prevPosIdentifierKey = key;
        this.prevType = type;

        return sharedResult$;
    }

    private createSharedFetching<T>(input$: Observable<T>): Observable<T> {
        const shared$ = input$.pipe(
            takeUntil(this.destroy$),
            shareReplay({bufferSize: 1, refCount: true})
        );

        this.prevSub?.unsubscribe();
        this.prevSub = shared$.subscribe();

        return shared$;
    }

    private getStream(
        posIdentifier: HeldPositionIdentifier | undefined,
        type: PosFramesHistoryType
    ): Observable<number[] | undefined> {
        if (!posIdentifier || !type) {
            return of(undefined);
        }

        return this.dexPosFramesStreamingService.createSubscription(posIdentifier, type);
    }

    private getPosIdentifier(
        entity: Tranche | HeldPosition| LiquidityPool | undefined
    ): HeldPositionIdentifier | undefined {
        if (!entity) {
            return undefined;
        }

        const POS_ID_DERIVED = 'derived';

        switch (true) {
            case !!(entity as HeldPosition).identifier:
                return (entity as HeldPosition).identifier;
            case !!(entity as Tranche).poolTrancheId:
                return {
                    accountId:     (entity as Tranche).reservedAccountId,
                    deliverableId: (entity as Tranche).unitDeliverableId,
                    positionId:    POS_ID_DERIVED
                } satisfies HeldPositionIdentifier;
            case !!(entity as LiquidityPool).liquidityPoolId:
                return {
                    accountId:     (entity as LiquidityPool).reservedAccountId,
                    deliverableId: (entity as LiquidityPool).unitDeliverableId,
                    positionId:    POS_ID_DERIVED
                } satisfies HeldPositionIdentifier;
        }

        return undefined;
    }

}
